import React, { useState, useEffect } from 'react';
import { Container, Button, TextField, Box, InputAdornment, IconButton, Alert, Typography, CircularProgress } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from './axios';
import { validatePassword, getPasswordStrength } from '../../utils/passwordValidation';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(null); // null = checking, true = valid, false = invalid
  const [tokenChecking, setTokenChecking] = useState(true);

  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenValid(false);
        setTokenChecking(false);
        return;
      }

      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/verify-reset-token/${token}`);
        setTokenValid(response.data.valid);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setTokenChecking(false);
      }
    };

    verifyToken();
  }, [token]);

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    
    if (newPassword.length === 0) {
      setPasswordError('');
      setPasswordStrength('');
      return;
    }
    
    const errors = validatePassword(newPassword);
    
    if (errors.length > 0) {
      setPasswordError(errors.join(', '));
      setPasswordStrength('weak');
    } else {
      setPasswordError('');
      setPasswordStrength(getPasswordStrength(newPassword));
    }
    
    // Check match if confirm password is already filled
    if (confirmPassword && newPassword !== confirmPassword) {
      setError('Lösenorden matchar inte');
    } else if (confirmPassword && newPassword === confirmPassword) {
      setError('');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    
    if (newConfirmPassword && newConfirmPassword !== password) {
      setError('Lösenorden matchar inte');
    } else {
      setError('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    // Validate password
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setError(`Lösenordet uppfyller inte kraven: ${passwordErrors.join(', ')}`);
      setLoading(false);
      return;
    }

    // Check password match
    if (password !== confirmPassword) {
      setError('Lösenorden matchar inte');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/reset-password`, {
        token,
        password,
      });

      if (response.status === 200) {
        setSuccess(true);
      }
    } catch (error) {
      console.error('Reset password error', error);
      if (error.response && error.response.data && error.response.data.message) {
        setError(error.response.data.message);
      } else {
        setError('Ett fel uppstod. Försök igen.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking token
  if (tokenChecking) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Verifierar länk...</Typography>
      </Container>
    );
  }

  // Show error if token is invalid
  if (!tokenValid) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{ 
          backgroundColor: '#fff', 
          borderRadius: 3, 
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          padding: { xs: '24px', md: '40px' },
        }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: '#1A237E',
              marginBottom: '16px',
              textAlign: 'center',
            }}
          >
            Ogiltig länk
          </Typography>
          <Alert severity="error" sx={{ marginBottom: '24px' }}>
            Återställningslänken är ogiltig eller har gått ut. Begär en ny länk för att återställa ditt lösenord.
          </Alert>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/info')}
            sx={{
              backgroundColor: '#1565C0',
              color: '#fff',
              textTransform: 'none',
              padding: '12px',
              fontSize: '1rem',
              fontWeight: 500,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#0d47a1',
              },
            }}
          >
            Tillbaka till startsidan
          </Button>
        </Box>
      </Container>
    );
  }

  // Show success message
  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{ 
          backgroundColor: '#fff', 
          borderRadius: 3, 
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          padding: { xs: '24px', md: '40px' },
        }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: '#1A237E',
              marginBottom: '16px',
              textAlign: 'center',
            }}
          >
            Lösenord återställt
          </Typography>
          <Alert severity="success" sx={{ marginBottom: '24px' }}>
            Ditt lösenord har återställts. Du kan nu logga in med ditt nya lösenord.
          </Alert>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/info')}
            sx={{
              backgroundColor: '#1565C0',
              color: '#fff',
              textTransform: 'none',
              padding: '12px',
              fontSize: '1rem',
              fontWeight: 500,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#0d47a1',
              },
            }}
          >
            Gå till inloggning
          </Button>
        </Box>
      </Container>
    );
  }

  // Show password reset form
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Box sx={{ 
        backgroundColor: '#fff', 
        borderRadius: 3, 
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        padding: { xs: '24px', md: '40px' },
      }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: '#1A237E',
            marginBottom: '8px',
            textAlign: 'center',
          }}
        >
          Återställ lösenord
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#666',
            marginBottom: '24px',
            textAlign: 'center',
          }}
        >
          Ange ditt nya lösenord nedan.
        </Typography>

        <form onSubmit={handleSubmit}>
          <Box mb={2}>
            <TextField
              label="Nytt lösenord"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handlePasswordChange}
              required
              fullWidth
              error={!!passwordError}
              helperText={
                passwordError 
                  ? passwordError 
                  : passwordStrength === 'strong' 
                    ? 'Starkt lösenord' 
                    : passwordStrength === 'medium' 
                      ? 'Medelstarkt lösenord' 
                      : passwordStrength === 'good' 
                        ? 'Bra lösenord' 
                        : 'Minst 8 tecken, stor och liten bokstav, siffra och specialtecken'
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
          <Box mb={2}>
            <TextField
              label="Bekräfta lösenord"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              required
              fullWidth
              error={!!error && error.includes('matchar inte')}
              helperText={error && error.includes('matchar inte') ? error : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
          {error && !error.includes('matchar inte') && (
            <Alert severity="error" sx={{ marginBottom: '16px' }}>
              {error}
            </Alert>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              backgroundColor: '#1565C0',
              color: '#fff',
              textTransform: 'none',
              padding: '12px',
              fontSize: '1rem',
              fontWeight: 500,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#0d47a1',
              },
            }}
          >
            {loading ? 'Återställer...' : 'Återställ lösenord'}
          </Button>
        </form>
      </Box>
    </Container>
  );
}
